import {
  httpClient,
  uploadFileWithTicket,
  type BinaryDownload,
} from "@bw/shared/api";
import type {
  MediaUploadResponse,
  operations,
  Schema,
} from "@bw/shared/types/generated";
import type {
  DrawCreate as GeneratedDrawCreate,
  DrawResponse as GeneratedDrawResponse,
  DrawUpdate as GeneratedDrawUpdate,
  TournamentQualificationOverrideCommand as GeneratedTournamentQualificationOverrideCommand,
  TournamentQualificationResponse as GeneratedTournamentQualificationResponse,
  TournamentQualificationSlot as GeneratedTournamentQualificationSlot,
  TournamentOperatorBracketPage as GeneratedTournamentOperatorBracketPage,
  TournamentOperatorBracketSection as GeneratedTournamentOperatorBracketSection,
} from "@bw/shared/types";

// Defensive list normalizer. Backend list endpoints occasionally return
// a non-array body (e.g. {detail: 'not found'}, {items: [...]}, or null)
// when state is degenerate. The UI relies on .map / for…of and crashes
// with "x.map is not a function" if the value isn't an array. Coerce
// any response into an array so callers can render an empty state
// instead of crashing the whole tab.
function asArray<T>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (
    value &&
    typeof value === "object" &&
    Array.isArray((value as { items?: unknown }).items)
  ) {
    return (value as { items: T[] }).items;
  }
  return [];
}

// Defensive Page<T> normalizer. Ensures .items is always an array even
// if the backend returns a degenerate or non-paginated body.
function asPage<T>(value: unknown): Page<T> {
  if (
    value &&
    typeof value === "object" &&
    Array.isArray((value as { items?: unknown }).items)
  ) {
    return value as Page<T>;
  }
  if (Array.isArray(value)) {
    return {
      items: value as T[],
      total: value.length,
      page: 1,
      page_size: value.length,
    };
  }
  return { items: [], total: 0, page: 1, page_size: 0 };
}

export type TournamentType = "individual" | "team";
export type PricingModel = "per_event" | "tiered_volume";
export type TournamentStatus =
  | Schema["TournamentResponse"]["status"]
  | "open"
  | "cancelled";
export type Money = string | number;
// Canonical post-A2: TYPE is singles | doubles (Mixed moved to the gender axis).
// NOTE: these are legacy hand-typed unions — migrate to @bw/shared generated
// (TournamentEventResponse) in a follow-up; extended here for slice A5a.
export type TournamentEventMode = "singles" | "doubles";
export type TournamentEventGender =
  | "male"
  | "female"
  | "mixed"
  | "boys"
  | "girls"
  | "any";
export type DrawFormat = Schema["DrawCreate"]["format"];
export type DrawStage = "main_draw" | string;
export type ScoringFormat = "21x3" | "11x5" | "15x3" | "21x5" | "15x5";

export type TournamentPrintableArtifactKind =
  Schema["TournamentPrintableArtifactKind"];
export type TournamentPrintableDrawCapabilities =
  Schema["TournamentPrintableDrawCapabilities"];
type TournamentPrintableArtifactOperation =
  operations["download_tournament_printable_artifact_tournaments__tournament_id__printables__artifact_kind__pdf_get"];
export type TournamentPrintableArtifactTarget = NonNullable<
  TournamentPrintableArtifactOperation["parameters"]["query"]
>;
export type TournamentPrintableArtifactRequest = {
  kind: TournamentPrintableArtifactKind;
} & TournamentPrintableArtifactTarget;

export type RefundTier = Record<string, unknown>;

export type ScoringSystemConfig = Required<Schema["ScoringSystemConfig"]>;

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

export type Tournament = Schema["TournamentResponse"];
export type { MediaUploadResponse };
export type TournamentCreate = Schema["TournamentCreate"];
export type TournamentUpdate = Schema["TournamentUpdate"];
type TournamentCloseoutSchema = Schema["TournamentCloseoutResponse"];
export type TournamentCloseout = TournamentCloseoutSchema;
export type TournamentCloseoutFinance = Schema["TournamentSettlementResponse"];
export type TournamentOperationalRetrospective =
  Schema["TournamentOperationalRetrospectiveResponse"];
export type TournamentArchiveCommand = Schema["TournamentArchiveCommand"];
export type TournamentArchive = Schema["TournamentArchiveResponse"];
export type TournamentArchiveReleaseCommand =
  Schema["TournamentArchiveReleaseCommand"];
export type TournamentArchiveRelease =
  Schema["TournamentArchiveReleaseResponse"];
export type TournamentArchiveCertificateIssueCommand =
  Schema["TournamentArchiveCertificateIssueCommand"];
export type TournamentArchiveCertificateIssue =
  Schema["TournamentArchiveCertificateIssueResponse"];
export type TournamentResultCertificationCommand =
  Schema["TournamentResultCertificationCommand"];
export type TournamentResultCertification =
  Schema["TournamentResultCertificationResponse"];
export type TournamentCertifiedScoreCorrectionCommand =
  Schema["TournamentCertifiedScoreCorrectionCommand"];
export type TournamentCertifiedScoreCorrection =
  Schema["TournamentCertifiedScoreCorrectionResponse"];
export type TournamentCompletionCommand = Schema["TournamentCompletionCommand"];
export type TournamentCompletion = Schema["TournamentCompletionResponse"];
export type TournamentRankingReconciliation =
  Schema["TournamentRankingReconciliationResponse"];
export type TournamentComplianceRequirementOverrides =
  Schema["TournamentComplianceRequirementOverrides"];

export interface TournamentEventCreate {
  name: string;
  mode?: TournamentEventMode | null;
  gender?: TournamentEventGender | null;
  // Optional DISPLAY-ONLY skill BAND label (A/B/C/D/J; J = junior). Decoupled
  // from eligibility — the numeric min_tier/max_tier still gate entry. The
  // backend stores it verbatim (common/models.py validate_grade); the FE never
  // derives it from mode/gender/tier.
  grade?: string | null;
  min_age?: number | null;
  max_age?: number | null;
  min_tier?: number | null;
  max_tier?: number | null;
  max_entries?: number | null;
  minimum_entries?: number | null;
  entry_fee?: Money | null;
  tech_fee?: Money | null;
  currency?: string;
  prize_first?: Money | null;
  prize_second?: Money | null;
  prize_third?: Money | null;
  refund_policy_summary?: string | null;
  legal_notes?: string | null;
  // #1421: when true, an age/gender-eligible registration is accepted
  // immediately ('registered'); when false (default) it is held in
  // 'pending_review' for the organizer to accept/decline. Tier eligibility is
  // advisory only (a tier mismatch flags the entry for review, never blocks).
  auto_accept_registrations?: boolean;
  display_order?: number;
}

export type TournamentEventUpdate = Partial<TournamentEventCreate>;

export interface TournamentEvent {
  event_id: string;
  tournament_id: string;
  name: string;
  mode: TournamentEventMode | null;
  gender: TournamentEventGender | null;
  // DISPLAY-ONLY skill BAND label (A/B/C/D/J), read off the response and
  // rendered verbatim. null when the event has no band. Decoupled from the
  // numeric min_tier/max_tier eligibility gate. Always present on the response
  // (the backend serializes it as null when unset), so it is non-optional here.
  grade: string | null;
  min_age: number | null;
  max_age: number | null;
  min_tier: number | null;
  max_tier: number | null;
  max_entries: number | null;
  minimum_entries: number | null;
  entry_fee: Money | null;
  tech_fee: Money | null;
  currency: string;
  prize_first: Money | null;
  prize_second: Money | null;
  prize_third: Money | null;
  refund_policy_summary: string | null;
  legal_notes: string | null;
  // #1421: organizer auto-accept policy. false (default) = registrations held
  // in pending_review for manual accept/decline. Always present on the response.
  auto_accept_registrations: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
  draw_count: number;
  entry_count: number;
  // Backend-derived TP presentation code (MS/WS/MD/WD/XD/BS/GS/BD/GD), or null
  // when the (mode, gender) combination has no code. Computed by the backend
  // (_event_code_for) — the single source of truth; the FE renders it VERBATIM
  // and NEVER recomputes from mode/gender.
  event_code: string | null;
}

export type DrawCreate = GeneratedDrawCreate;
export type DrawUpdate = GeneratedDrawUpdate;
export type Draw = Omit<
  Pick<
    GeneratedDrawResponse,
    | "draw_id"
    | "event_id"
    | "name"
    | "format"
    | "scoring_format"
    | "registered_entries"
    | "pending_entries"
    | "accepted_entries"
    | "paid_entries"
    | "unique_player_count"
    | "bracket_match_count"
    | "confirmation_status"
    | "status"
    | "created_at"
    | "stage"
    | "is_consolation"
    | "consolation_label"
    | "display_order"
    | "size"
    | "group_count"
    | "advance_per_group"
    | "minimum_matches"
    | "seeding_mode"
    | "qualification_state"
    | "qualification_revision"
    | "qualification_blocker"
    | "qualified_entry_count"
  >,
  | "group_count"
  | "advance_per_group"
  | "minimum_matches"
  | "seeding_mode"
  | "qualification_state"
  | "qualification_revision"
  | "qualification_blocker"
  | "qualified_entry_count"
> &
  Partial<
    Pick<
      GeneratedDrawResponse,
      | "group_count"
      | "advance_per_group"
      | "minimum_matches"
      | "seeding_mode"
      | "qualification_state"
      | "qualification_revision"
      | "qualification_blocker"
      | "qualified_entry_count"
    >
  >;

// Round-robin STANDINGS (#1399). The backend computes these on read (never
// persisted) and returns a StandingsResponse envelope. The generated barrel
// carries these under Schema["StandingRow"] etc. but does not (yet) re-export
// named aliases, so — like Draw / DrawEntry above — we declare local interfaces
// that mirror common/models.py:{StandingRow,GroupStandings,StandingsResponse}
// EXACTLY. Tallies + rank are server-computed; the FE renders them verbatim and
// never does standings math.
export interface StandingRow {
  entry_id: string;
  display_name: string | null;
  seed: number | null;
  played: number;
  wins: number;
  losses: number;
  games_won: number;
  games_lost: number;
  games_diff: number;
  points_won: number;
  points_lost: number;
  points_diff: number;
  rank: number;
}

export interface GroupStandings {
  group_number: number;
  rows: StandingRow[];
}

export interface StandingsResponse {
  draw_id: string;
  format: string;
  group_count: number;
  groups: GroupStandings[];
}

export type TournamentQualificationSlot = GeneratedTournamentQualificationSlot;
export type TournamentQualificationResponse =
  GeneratedTournamentQualificationResponse;
export type TournamentQualificationOverrideCommand =
  GeneratedTournamentQualificationOverrideCommand;
export type DrawSeedingPolicyCommand = Schema["DrawSeedingPolicyCommand"];
export type DrawSeedingPolicyResponse = Schema["DrawSeedingPolicyResponse"];

export type TournamentRegistrationCreate =
  Schema["TournamentRegistrationCreate"];

export type TournamentRegistrationQuote =
  Schema["TournamentRegistrationQuote-Output"];
export type TournamentOperatorEntryResponse =
  Schema["TournamentOperatorEntryResponse"];
// The mutation response intentionally contains only player-safe status facts.
// Operator finance/compliance detail is loaded from the separate, RBAC-scoped
// operator-entries read rather than echoed from a registration that accepts
// guardian contact.
export type TournamentPlayerRegistrationResponse =
  Schema["TournamentPlayerRegistrationResponse"];

export interface ForeignRepresentationCountry {
  country: string;
  period?: string | null;
  last_event?: string | null;
}

export interface ForeignRepresentationDeclaration {
  declared: boolean;
  countries: ForeignRepresentationCountry[];
}

export interface DrawEntry {
  entry_id: string;
  draw_id: string;
  player_id: string;
  partner_id: string | null;
  seed: number | null;
  status: string;
  payment_status: string;
  refund_status: string;
  rejection_reason: string | null;
  amount_paid: Money;
  currency: string;
  charge_transaction_id: string | null;
  platform_commission_rate: Money | null;
  platform_commission_amount: Money | null;
  net_payout_amount: Money | null;
  legal_acknowledged_at: string | null;
  guardian_name: string | null;
  guardian_email: string | null;
  guardian_consent_acknowledged_at: string | null;
  age_verification_document_url: string | null;
  age_verified_at: string | null;
  registered_at: string;
  transaction_id: string | null;
  waitlist_position: number | null;
  player_name: string | null;
  // The generic draw-entry read is a public/redacted projection and uses
  // `player_display_name` (NOT `player_name`). Legacy DrawEntryResponse paths
  // populate `player_name` instead, so both fields coexist. Finance-capable
  // Business code uses listOperatorEntries and never falls back to player_id.
  player_display_name?: string | null;
  partner_name?: string | null;
  foreign_representation: ForeignRepresentationDeclaration | null;
  for_review: boolean;
  for_review_reason: string | null;
}

export type DrawEntryResponse = DrawEntry;

export type SeedCreate = Schema["SeedCreate"];
export type SeedResponse = Schema["SeedResponse"];

export type SponsorTier = "title" | "gold" | "silver" | "bronze";

export interface SponsorCreate {
  name: string;
  tier: SponsorTier;
  logo_url?: string | null;
  website_url?: string | null;
  display_order?: number;
}

export interface SponsorResponse {
  sponsor_id: string;
  tournament_id: string;
  name: string;
  tier: SponsorTier;
  logo_url: string | null;
  website_url: string | null;
  display_order: number;
  created_at: string;
}

export type WinnerPrizeType = "cash" | "medal" | "merchandise" | "none";

export type WinnerCreate = Schema["WinnerCreate"];
export type TournamentWinnerPublicResponse =
  Schema["TournamentWinnerPublicResponse"];

// The REAL, prod-available operator on-behalf-of candidate lookup (#1436),
// replacing the staging-only mock-players bypass. `user_id` is the opaque
// Firebase uid — the registration KEY fed to registerEntry, never shown as a
// name. `display_name` is the human label the UI renders; it is null (FE
// em-dash) when withheld (a private minor's name, or an unresolved profile) —
// never the raw uid. Typed straight from the generated backend schema.
export type RegistrationCandidate = Schema["TournamentRegistrationCandidate"];
export type RegistrationCandidateListResponse =
  Schema["TournamentRegistrationCandidateListResponse"];

export interface TournamentEntryActionResponse {
  action: string;
  message: string;
  refund_amount: Money;
  currency: string;
  entry: DrawEntry;
}

// #1690: ack for resending a held minor entry's guardian-consent email. Sourced
// from the generated OpenAPI schema (no hand-written mirror) per ADR-005.
export type GuardianConsentResendResponse =
  Schema["GuardianConsentResendResponse"];

export interface EntryWithdrawRequest {
  reason?: string | null;
  medical_certificate_url?: string | null;
}

// POST /tournaments/entries/{id}/withdraw body (#1402 Phase 5). Mirrors the
// booking-cancel money path: an optional operator reason and an optional
// adjustable refund the operator may set only when they hold
// facility:refund_override. The override is OMITTED unless it deviates from the
// server-computed policy refund (clean migration — no fabricated zero).
export interface TournamentWithdrawRequest {
  reason?: string | null;
  refund_amount_override?: Money | null;
}

// GET /tournaments/entries/{id}/withdrawal-preview response (#1402 Phase 5).
// The SERVER-computed default refund the withdraw-confirm dialog reads BEFORE
// confirming — never compute the tier % client-side. `policy_refund` is the
// default refund (capped at `amount_paid`); past the deadline it is 0 and
// `withdrawal_closed` is true (an honest "no refund" read). `amount_paid` +
// `currency` let the FE bound the override field to [0, amount_paid].
export interface TournamentWithdrawalPreview {
  policy_refund: Money;
  amount_paid: Money;
  currency: string;
  withdrawal_closed: boolean;
}

export interface TournamentEntryRejectionRequest {
  reason?: string | null;
}

export interface MatchGameScore {
  p1: number;
  p2: number;
}
export interface MatchScoreReport {
  score: string;
  winner_entry_id: string;
}
export interface MatchScheduleRequest {
  court_id: string;
  scheduled_at: string;
}
export type BracketMatch = Schema["BracketMatchResponse"];
export type TournamentOperatorBracketSection =
  GeneratedTournamentOperatorBracketSection;
export type TournamentOperatorBracketPage =
  GeneratedTournamentOperatorBracketPage;

export interface TournamentOperatorBracketPageParams {
  sectionKey?: string;
  cursor?: string;
  limit?: number;
}
export type TeamMatch = Schema["TeamMatchResponse"] & {
  lineup_revision: number;
};
export type Rubber = Schema["RubberResponse"];
export interface TeamMatchScoreUpdate {
  expected_lineup_revision: number;
  outcome: "win" | "walkover" | "retired";
  score_away: number;
  score_home: number;
  winner_side?: "home" | "away" | null;
}
export interface RubberScoreUpdate {
  expected_lineup_revision: number;
  outcome: "win" | "walkover" | "retired";
  score_away: number;
  score_home: number;
  winner_side: "home" | "away";
}
export interface RubberLineupInput {
  rubber_number: number;
  mode: "singles" | "doubles" | "mixed_doubles";
  home_player1_id: string;
  home_player2_id?: string | null;
  away_player1_id: string;
  away_player2_id?: string | null;
}
export interface TeamMatchLineupUpdate {
  expected_revision: number;
  rubbers: RubberLineupInput[];
}
export interface TeamMatchLineupResponse {
  team_match_id: string;
  revision: number;
  rubbers: Rubber[];
}
export type TournamentCourtCreate = Schema["TournamentCourtCreate"];
export type TournamentCourt = Schema["TournamentCourtResponse"];

export interface TournamentSeriesCreate {
  name: string;
  facility_id: string;
}

export interface TournamentSeriesResponse {
  series_id: string;
  name: string;
  facility_id: string;
  members: Record<string, unknown>[];
  created_at: string;
}

export interface TournamentSeriesAdvancementResponse {
  advancement_id: string;
  series_id: string;
  player_id: string;
  player_display_name?: string | null;
  from_tournament_id: string;
  from_tournament_name?: string | null;
  to_tournament_id: string | null;
  to_tournament_name?: string | null;
  placement: number;
  is_eligible_for_next: boolean;
  advanced_at: string;
}

export type TournamentPublishReadinessCheck =
  Schema["TournamentPublishReadinessCheckResponse"];
export type TournamentPublishReadiness =
  Schema["TournamentPublishReadinessResponse"];
export type TournamentOperatorPlaybook =
  Schema["TournamentOperatorPlaybookResponse"];

export interface TeamCreate {
  event_id: string;
  name: string;
  captain_user_id: string;
  facility_id?: string | null;
}

export interface TeamUpdate {
  name?: string | null;
  facility_id?: string | null;
}

export interface TeamResponse {
  team_id: string;
  event_id: string;
  name: string;
  captain_user_id: string;
  captain_display_name?: string | null;
  facility_id: string | null;
  created_at: string;
  member_count: number;
}

export interface TeamMemberCreate {
  user_id: string;
  role?: "captain" | "player";
}

export interface TeamMemberResponse {
  team_id: string;
  user_id: string;
  display_name?: string | null;
  user_email?: string | null;
  role: string;
  joined_at: string;
}

export interface TeamDrawEntryCreate {
  draw_id: string;
  seed_label?: string | null;
}

export interface TeamDrawEntryResponse {
  entry_id: string;
  team_id: string;
  draw_id: string;
  status: string;
  seed_label: string | null;
  registered_at: string;
  withdrawn_at: string | null;
}

export type CoachRegistrationCreate = Schema["CoachRegistrationCreate"];
export type CoachRegistration = Schema["CoachRegistrationResponse"];

export type TournamentStaffRole =
  | "tournament_director"
  | "referee"
  | "deputy_referee"
  | "technical_official"
  | "line_judge_coordinator";

export interface TournamentStaffCreate {
  tournament_id: string;
  user_id?: string | null;
  role: TournamentStaffRole;
  contact_email?: string | null;
  contact_phone?: string | null;
  display_publicly?: boolean;
}

export interface TournamentStaff {
  staff_id: string;
  tournament_id: string;
  user_id: string | null;
  display_name?: string | null;
  role: TournamentStaffRole;
  contact_email: string | null;
  contact_phone: string | null;
  display_publicly: boolean;
  created_at: string;
  updated_at: string;
}

export interface MediaCredentialApply {
  applicant_name: string;
  organization: string;
  email: string;
}

export interface MediaCredential {
  credential_id: string;
  tournament_id: string;
  applicant_name: string;
  organization: string;
  email: string;
  status: "pending" | "approved" | "denied" | string;
  decision_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface LegalDocumentCreate {
  doc_type: string;
  title: string;
  body: string;
  requires_signature?: boolean;
  requires_guardian_signature_when_minor?: boolean;
}

export interface LegalDocument {
  document_id: string;
  tournament_id: string;
  doc_type: string;
  title: string;
  body: string;
  requires_signature: boolean;
  requires_guardian_signature_when_minor: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

export type CertificateTemplateCreate = Schema["CertificateTemplateCreate"];
export type CertificateTemplate = Schema["CertificateTemplateResponse"];

export interface MerchandiseCreate {
  name: string;
  price: Money;
  sku_label?: string | null;
  options?: Record<string, unknown> | null;
}

export interface Merchandise {
  sku: string;
  tournament_id: string;
  name: string;
  price: Money;
  sku_label: string | null;
  options: Record<string, unknown> | null;
  active: boolean;
  created_at: string;
}

export interface MerchandiseOrderCreate {
  sku: string;
  quantity?: number;
  selected_option?: string | null;
}

export interface MerchandiseOrder {
  order_id: string;
  tournament_id: string;
  draw_entry_id: string | null;
  user_id: string;
  sku: string;
  quantity: number;
  selected_option: string | null;
  unit_price: Money;
  created_at: string;
}

function pageQuery(page?: number, pageSize?: number): string {
  const params = new URLSearchParams();

  if (page !== undefined) params.set("page", String(page));
  if (pageSize !== undefined) params.set("page_size", String(pageSize));

  const query = params.toString();

  return query ? `?${query}` : "";
}

function notesQuery(notes?: string | null): string {
  if (!notes) return "";

  return `?${new URLSearchParams({ notes }).toString()}`;
}

function candidatesQuery(
  drawId: string | null,
  query?: string,
  limit?: number,
  pairedWithUserId?: string,
): string {
  const params = new URLSearchParams();
  const trimmed = query?.trim();

  if (drawId) params.set("draw_id", drawId);
  if (trimmed) params.set("query", trimmed);
  if (limit !== undefined) params.set("limit", String(limit));
  if (pairedWithUserId) params.set("paired_with_user_id", pairedWithUserId);

  const qs = params.toString();

  return qs ? `?${qs}` : "";
}

export function listTournaments(facilityId: string): Promise<Tournament[]> {
  return httpClient
    .get<unknown>(`/tournaments/facilities/${facilityId}`)
    .then(asArray<Tournament>);
}

export function getTournament(tournamentId: string): Promise<Tournament> {
  return httpClient.get<Tournament>(`/tournaments/${tournamentId}`);
}

export function getTournamentCloseout(
  tournamentId: string,
): Promise<TournamentCloseout> {
  return httpClient.get<TournamentCloseout>(
    `/tournaments/${tournamentId}/closeout`,
  );
}

/**
 * Read-only post-event facts bound by the server to the governed completion or
 * archive revision. The browser renders these facts; it does not reconstruct
 * event operations, settlement, or delivery state.
 */
export function getTournamentOperationalRetrospective(
  tournamentId: string,
): Promise<TournamentOperationalRetrospective> {
  return httpClient.get<TournamentOperationalRetrospective>(
    `/tournaments/${tournamentId}/closeout/retrospective`,
  );
}

export function certifyTournamentResults(
  tournamentId: string,
  command: TournamentResultCertificationCommand,
): Promise<TournamentResultCertification> {
  return httpClient.post<TournamentResultCertification>(
    `/tournaments/${tournamentId}/closeout/certifications`,
    command,
  );
}

export function correctCertifiedTournamentScore(
  tournamentId: string,
  command: TournamentCertifiedScoreCorrectionCommand,
): Promise<TournamentCertifiedScoreCorrection> {
  return httpClient.post<TournamentCertifiedScoreCorrection>(
    `/tournaments/${tournamentId}/closeout/corrections`,
    command,
  );
}

export function completeTournamentCloseout(
  tournamentId: string,
  command: TournamentCompletionCommand,
): Promise<TournamentCompletion> {
  return httpClient.post<TournamentCompletion>(
    `/tournaments/${tournamentId}/closeout/complete`,
    command,
  );
}

export function archiveTournamentCloseout(
  tournamentId: string,
  command: TournamentArchiveCommand,
): Promise<TournamentArchive> {
  return httpClient.post<TournamentArchive>(
    `/tournaments/${tournamentId}/closeout/archive`,
    command,
  );
}

export function getTournamentArchiveRelease(
  tournamentId: string,
): Promise<TournamentArchiveRelease> {
  return httpClient.get<TournamentArchiveRelease>(
    `/tournaments/${tournamentId}/closeout/archive/release`,
  );
}

export function releaseTournamentArchive(
  tournamentId: string,
  command: TournamentArchiveReleaseCommand,
): Promise<TournamentArchiveRelease> {
  return httpClient.post<TournamentArchiveRelease>(
    `/tournaments/${tournamentId}/closeout/archive/release`,
    command,
  );
}

export function downloadTournamentOfficialReport(
  tournamentId: string,
): Promise<BinaryDownload> {
  return httpClient.getBlob(
    `/tournaments/${encodeURIComponent(tournamentId)}/closeout/archive/report.pdf`,
  );
}

export function issueTournamentArchiveCertificates(
  tournamentId: string,
  command: TournamentArchiveCertificateIssueCommand,
): Promise<TournamentArchiveCertificateIssue> {
  return httpClient.post<TournamentArchiveCertificateIssue>(
    `/tournaments/${tournamentId}/closeout/archive/certificates`,
    command,
  );
}

export function createTournament(
  facilityId: string,
  data: TournamentCreate,
): Promise<Tournament> {
  return httpClient.post<Tournament>(
    `/tournaments/facilities/${facilityId}`,
    data,
  );
}

export function updateTournament(
  tournamentId: string,
  data: TournamentUpdate,
): Promise<Tournament> {
  return httpClient.patch<Tournament>(`/tournaments/${tournamentId}`, data);
}

export function deleteTournament(tournamentId: string): Promise<void> {
  return httpClient.delete<void>(`/tournaments/${tournamentId}`);
}

export function publishTournament(tournamentId: string): Promise<Tournament> {
  return httpClient.post<Tournament>(`/tournaments/${tournamentId}/publish`);
}

export function getPublishReadiness(
  tournamentId: string,
): Promise<TournamentPublishReadiness> {
  return httpClient.get<TournamentPublishReadiness>(
    `/tournaments/${tournamentId}/publish-readiness`,
  );
}

export function getTournamentOperatorPlaybook(
  tournamentId: string,
): Promise<TournamentOperatorPlaybook> {
  return httpClient.get<TournamentOperatorPlaybook>(
    `/tournaments/${tournamentId}/operator-playbook`,
  );
}

export function cancelTournament(tournamentId: string): Promise<void> {
  return httpClient.post<void>(`/tournaments/${tournamentId}/cancel`);
}

export function createTournamentSeries(
  data: TournamentSeriesCreate,
): Promise<TournamentSeriesResponse> {
  return httpClient.post<TournamentSeriesResponse>("/tournaments/series", data);
}

export function listTournamentSeries(
  facilityId: string,
): Promise<TournamentSeriesResponse[]> {
  const query = `?${new URLSearchParams({ facility_id: facilityId }).toString()}`;

  return httpClient
    .get<unknown>(`/tournaments/series${query}`)
    .then(asArray<TournamentSeriesResponse>);
}

export function addTournamentToSeries(
  seriesId: string,
  tournamentId: string,
  data: { sequence_order: number; advancement_quota?: number | null },
): Promise<TournamentSeriesResponse> {
  return httpClient.post<TournamentSeriesResponse>(
    `/tournaments/series/${seriesId}/tournaments/${tournamentId}`,
    data,
  );
}

export function computeSeriesAdvancements(
  seriesId: string,
  fromTournamentId: string,
): Promise<TournamentSeriesAdvancementResponse[]> {
  return httpClient
    .post<unknown>(
      `/tournaments/series/${seriesId}/tournaments/${fromTournamentId}/advancements`,
    )
    .then(asArray<TournamentSeriesAdvancementResponse>);
}

export function listSeriesAdvancements(
  seriesId: string,
): Promise<TournamentSeriesAdvancementResponse[]> {
  return httpClient
    .get<unknown>(`/tournaments/series/${seriesId}/advancements`)
    .then(asArray<TournamentSeriesAdvancementResponse>);
}

export function createEvent(
  tournamentId: string,
  data: TournamentEventCreate,
): Promise<TournamentEvent> {
  return httpClient.post<TournamentEvent>(
    `/tournaments/${tournamentId}/events`,
    {
      ...data,
      tournament_id: tournamentId,
    },
  );
}

export function listEvents(
  tournamentId: string,
  page?: number,
  pageSize?: number,
): Promise<Page<TournamentEvent>> {
  return httpClient
    .get<unknown>(
      `/tournaments/${tournamentId}/events${pageQuery(page, pageSize)}`,
    )
    .then(asPage<TournamentEvent>);
}

export function getEvent(eventId: string): Promise<TournamentEvent> {
  return httpClient.get<TournamentEvent>(`/events/${eventId}`);
}

export function updateEvent(
  eventId: string,
  data: TournamentEventUpdate,
): Promise<TournamentEvent> {
  return httpClient.patch<TournamentEvent>(`/events/${eventId}`, data);
}

export function deleteEvent(eventId: string): Promise<void> {
  return httpClient.delete<void>(`/events/${eventId}`);
}

export function createDraw(
  tournamentId: string,
  data: DrawCreate,
): Promise<Draw> {
  return httpClient.post<Draw>(`/tournaments/${tournamentId}/draws`, data);
}

export function listDraws(tournamentId: string): Promise<Draw[]> {
  return httpClient
    .get<unknown>(`/tournaments/${tournamentId}/draws`)
    .then(asArray<Draw>);
}

export function updateDraw(drawId: string, data: DrawUpdate): Promise<Draw> {
  return httpClient.patch<Draw>(`/tournaments/draws/${drawId}`, data);
}

// GET /tournaments/draws/{drawId}/standings — round-robin standings (#1399).
// The backend computes the tallies + rank on read and returns the typed
// StandingsResponse envelope; this thin client returns it verbatim. A 4xx
// (404 not found / 400 unsupported draw format) surfaces to the caller via the
// shared ApiError path — never swallowed.
export function getDrawStandings(drawId: string): Promise<StandingsResponse> {
  return httpClient.get<StandingsResponse>(
    `/tournaments/draws/${encodeURIComponent(drawId)}/standings`,
  );
}

// Operator-only qualification authority for group-to-knockout draws. The
// response is server-derived; the browser never calculates qualifiers or
// tiebreaks, and the override carries an explicit plan revision + reason.
export function getDrawQualification(
  drawId: string,
): Promise<TournamentQualificationResponse> {
  return httpClient.get<TournamentQualificationResponse>(
    `/tournaments/draws/${encodeURIComponent(drawId)}/qualification`,
  );
}

// Operator-only policy evidence. The random seed and revision remain a
// server-owned contract; Business displays them but never computes placement.
export function getDrawSeedingPolicy(
  drawId: string,
): Promise<DrawSeedingPolicyResponse> {
  return httpClient.get<DrawSeedingPolicyResponse>(
    `/tournaments/draws/${encodeURIComponent(drawId)}/seeding-policy`,
  );
}

export function applyDrawSeedingPolicy(
  drawId: string,
  command: DrawSeedingPolicyCommand,
): Promise<DrawSeedingPolicyResponse> {
  return httpClient.put<DrawSeedingPolicyResponse>(
    `/tournaments/draws/${encodeURIComponent(drawId)}/seeding-policy`,
    command,
  );
}

export function overrideDrawQualification(
  drawId: string,
  command: TournamentQualificationOverrideCommand,
): Promise<TournamentQualificationResponse> {
  return httpClient.post<TournamentQualificationResponse>(
    `/tournaments/draws/${encodeURIComponent(drawId)}/qualification/override`,
    command,
  );
}

// POST /tournaments/draws/{drawId}/register — the REAL admin/operator on-behalf
// registration path (#1436). Runs full eligibility / waiver / payment / consent
// on the server. `player_ids` carries the candidate user_id(s) (singles = one,
// doubles = [player, partner]). The operator dialog passes NO payment_method_id,
// so a PAID draw resolves to a structured 400 ("payment_method_id is required")
// which the caller surfaces honestly — it is NEVER swallowed as success. (The
// old `/entries` path was a GET-only route; this POST hits the register handler.)
export function registerEntry(
  drawId: string,
  data: TournamentRegistrationCreate,
): Promise<TournamentPlayerRegistrationResponse> {
  return httpClient.post<TournamentPlayerRegistrationResponse>(
    `/tournaments/draws/${drawId}/register`,
    data,
  );
}

export function getRegistrationQuote(
  drawId: string,
  playerId: string,
): Promise<TournamentRegistrationQuote> {
  return httpClient.get<TournamentRegistrationQuote>(
    `/tournaments/draws/${encodeURIComponent(drawId)}/registration-quote?player_id=${encodeURIComponent(playerId)}`,
  );
}

// GET /tournaments/{tournamentId}/registration-candidates — the prod-available,
// RBAC-scoped (facility:tournament) operator candidate lookup that feeds the real
// on-behalf registerEntry (#1436). Unlike the removed staging-only mock-players
// lookup, this is NOT env-gated. A 403 (insufficient permission) surfaces to the
// caller via the shared ApiError path — never swallowed. The server excludes
// unresolved and withheld identities so provider IDs never become display text.
export async function listRegistrationCandidates(
  tournamentId: string,
  drawId: string | null,
  query?: string,
  limit?: number,
  pairedWithUserId?: string,
): Promise<RegistrationCandidate[]> {
  const response = await httpClient.get<unknown>(
    `/tournaments/${tournamentId}/registration-candidates${candidatesQuery(drawId, query, limit, pairedWithUserId)}`,
  );

  return asArray<RegistrationCandidate>(response);
}

export function listEntries(drawId: string): Promise<DrawEntry[]> {
  return httpClient
    .get<unknown>(`/draws/${encodeURIComponent(drawId)}/entries`)
    .then(asArray<DrawEntry>);
}

// The public `/entries` projection intentionally excludes payment facts.  The
// Business registrations ledger uses this explicitly authorized route instead
// so every displayed amount is a durable server snapshot, never browser math.
export function listOperatorEntries(
  drawId: string,
): Promise<TournamentOperatorEntryResponse[]> {
  return httpClient.get<TournamentOperatorEntryResponse[]>(
    `/tournaments/draws/${encodeURIComponent(drawId)}/operator-entries`,
  );
}

export function listSeeds(drawId: string): Promise<SeedResponse[]> {
  return httpClient
    .get<unknown>(`/draws/${drawId}/seeds`)
    .then(asArray<SeedResponse>);
}

export function createSeed(
  drawId: string,
  data: SeedCreate,
): Promise<SeedResponse> {
  return httpClient.post<SeedResponse>(
    `/tournaments/draws/${drawId}/seeds`,
    data,
  );
}

export function deleteSeed(seedId: string): Promise<void> {
  return httpClient.delete<void>(`/tournaments/seeds/${seedId}`);
}

export function listSponsors(tournamentId: string): Promise<SponsorResponse[]> {
  return httpClient
    .get<unknown>(`/tournaments/${tournamentId}/sponsors`)
    .then(asArray<SponsorResponse>);
}

export function createSponsor(
  tournamentId: string,
  data: SponsorCreate,
): Promise<SponsorResponse> {
  return httpClient.post<SponsorResponse>(
    `/tournaments/${tournamentId}/sponsors`,
    data,
  );
}

export function deleteSponsor(sponsorId: string): Promise<void> {
  return httpClient.delete<void>(`/tournaments/sponsors/${sponsorId}`);
}

export function deleteLegalDocument(documentId: string): Promise<void> {
  return httpClient.delete<void>(`/tournaments/legal-documents/${documentId}`);
}

export function deleteCertificateTemplate(templateId: string): Promise<void> {
  return httpClient.delete<void>(
    `/tournaments/certificate-templates/${templateId}`,
  );
}

export function deleteMerchandise(sku: string): Promise<void> {
  return httpClient.delete<void>(`/tournaments/merchandise/${sku}`);
}

export function deleteCoach(coachEntryId: string): Promise<void> {
  return httpClient.delete<void>(`/tournaments/coaches/${coachEntryId}`);
}

export function createWinner(
  tournamentId: string,
  data: WinnerCreate,
): Promise<TournamentWinnerPublicResponse> {
  return httpClient.post<TournamentWinnerPublicResponse>(
    `/tournaments/${tournamentId}/winners`,
    data,
  );
}

export function listWinners(
  tournamentId: string,
): Promise<TournamentWinnerPublicResponse[]> {
  return httpClient
    .get<unknown>(`/tournaments/${tournamentId}/winners`)
    .then(asArray<TournamentWinnerPublicResponse>);
}

/** Download the current published tournament schedule as a private PDF. */
export function downloadTournamentScheduleExport(
  tournamentId: string,
): Promise<BinaryDownload> {
  return httpClient.getBlob(
    `/tournaments/${encodeURIComponent(tournamentId)}/exports/schedule.pdf`,
  );
}

/** Download one typed, server-rendered Tournament operating-sheet PDF. */
export function downloadTournamentPrintableArtifact(
  tournamentId: string,
  request: TournamentPrintableArtifactRequest,
): Promise<BinaryDownload> {
  const query = new URLSearchParams();
  if (request.event_id) query.set("event_id", request.event_id);
  if (request.draw_id) query.set("draw_id", request.draw_id);
  const suffix = query.size ? `?${query.toString()}` : "";
  return httpClient.getBlob(
    `/tournaments/${encodeURIComponent(tournamentId)}/printables/${encodeURIComponent(request.kind)}.pdf${suffix}`,
  );
}

/** Read exact server-owned printable kinds for one selected draw. */
export function getTournamentPrintableDrawCapabilities(
  tournamentId: string,
  drawId: string,
): Promise<TournamentPrintableDrawCapabilities> {
  return httpClient.get<TournamentPrintableDrawCapabilities>(
    `/tournaments/${encodeURIComponent(tournamentId)}/draws/${encodeURIComponent(drawId)}/printable-capabilities`,
  );
}

/** Download one server-built, organizer-scoped printable match game sheet. */
export function downloadTournamentGameSheet(
  matchKind: "bracket_match" | "rubber",
  matchId: string,
): Promise<BinaryDownload> {
  return httpClient.getBlob(
    `/tournaments/matches/${matchKind}/${encodeURIComponent(matchId)}/game-sheet.pdf`,
  );
}

/** Build and download one current-plan QR-assisted game-sheet ZIP. */
export function generateTournamentGameSheetPackage(
  tournamentId: string,
  request: Schema["TournamentGameSheetPackageRequest"],
): Promise<BinaryDownload> {
  return httpClient.post<BinaryDownload>(
    `/tournaments/${encodeURIComponent(tournamentId)}/game-sheets/package.zip`,
    request,
    { responseType: "blob" },
  );
}

/** Resolve one opaque printed sheet to an in-memory canonical score-panel target. */
export function resolveTournamentScoreSheet(
  request: Schema["TournamentScoreSheetResolveRequest"],
): Promise<Schema["TournamentScoreSheetResolution"]> {
  return httpClient.post<Schema["TournamentScoreSheetResolution"]>(
    "/tournaments/score-sheets/resolve",
    request,
  );
}

export function listUndistributedPrizes(
  tournamentId: string,
): Promise<TournamentWinnerPublicResponse[]> {
  return httpClient
    .get<unknown>(`/tournaments/${tournamentId}/winners/undistributed`)
    .then(asArray<TournamentWinnerPublicResponse>);
}

export function markPrizeDistributed(
  winnerId: string,
): Promise<TournamentWinnerPublicResponse> {
  return httpClient.patch<TournamentWinnerPublicResponse>(
    `/tournaments/winners/${winnerId}/prize-distributed`,
  );
}

export function deleteWinner(winnerId: string): Promise<void> {
  return httpClient.delete<void>(`/tournaments/winners/${winnerId}`);
}

export function approveEntry(entryId: string): Promise<DrawEntry> {
  return httpClient.post<DrawEntry>(`/tournaments/entries/${entryId}/approve`);
}

export function rejectEntry(
  entryId: string,
  data: TournamentEntryRejectionRequest = {},
): Promise<TournamentEntryActionResponse> {
  return httpClient.post<TournamentEntryActionResponse>(
    `/tournaments/entries/${entryId}/reject`,
    data,
  );
}

// POST /tournaments/entries/{id}/resend-guardian-consent (#1690). Queues a
// parental-consent delivery for an entry held in pending_guardian_consent — for
// a guardian who lost the original. The backend re-enforces RBAC
// (facility:tournament) and the held-state guard, so a 403/404/409/422 must
// surface to the caller — never swallowed. No body; the entry is identified by id.
export function resendGuardianConsent(
  entryId: string,
): Promise<GuardianConsentResendResponse> {
  return httpClient.post<GuardianConsentResendResponse>(
    `/tournaments/entries/${entryId}/resend-guardian-consent`,
  );
}

// POST /tournaments/entries/{id}/withdraw (#1402 Phase 5 — migrated off the bare
// DELETE). Carries an optional operator reason + adjustable refund override; the
// backend re-enforces RBAC (facility:refund_override) and the [0, amount_paid]
// bound, so a 400/403/404 must surface to the caller — never swallowed.
export function withdrawEntry(
  entryId: string,
  body?: TournamentWithdrawRequest,
): Promise<TournamentEntryActionResponse> {
  return httpClient.post<TournamentEntryActionResponse>(
    `/tournaments/entries/${encodeURIComponent(entryId)}/withdraw`,
    body ?? {},
  );
}

// GET /tournaments/entries/{id}/withdrawal-preview — the SERVER-computed policy
// refund the withdraw-confirm dialog reads before confirming. A 404 (entry not
// found) surfaces to the caller; the client never fabricates a refund number.
export function previewWithdrawalRefund(
  entryId: string,
): Promise<TournamentWithdrawalPreview> {
  return httpClient.get<TournamentWithdrawalPreview>(
    `/tournaments/entries/${encodeURIComponent(entryId)}/withdrawal-preview`,
  );
}

export function generateBracket(drawId: string): Promise<BracketMatch[]> {
  return httpClient
    .post<unknown>(`/tournaments/draws/${drawId}/bracket`)
    .then(asArray<BracketMatch>);
}

export function advanceRound(drawId: string): Promise<{ next_round?: number }> {
  return httpClient.post<{ next_round?: number }>(
    `/tournaments/draws/${drawId}/advance-round`,
  );
}

export function getBracket(drawId: string): Promise<BracketMatch[]> {
  // Backend GET /draws/{id}/bracket (the public_event_router path the BFF
  // rewrites this URL to) returns a structured envelope
  // `{draw, tournament_type, matches: BracketMatch[]}` — not a bare array
  // and not the generic `{items: [...]}` shape asArray() understands.
  // Unwrap `matches` here; fall back to asArray() so any future divergence
  // (bare array, {items}) still degrades to an empty list instead of crashing.
  return httpClient
    .get<unknown>(`/tournaments/draws/${drawId}/bracket`)
    .then((v) => {
      if (
        v &&
        typeof v === "object" &&
        Array.isArray((v as { matches?: unknown }).matches)
      ) {
        return (v as { matches: BracketMatch[] }).matches;
      }
      return asArray<BracketMatch>(v);
    });
}

/**
 * Read one canonical graph section for the organizer console. Unlike the
 * public bracket reader this is facility-authorized and cursor-bounded, so the
 * Business app never downloads every match/entrant just to render navigation.
 */
export function getOperatorBracketPage(
  drawId: string,
  params: TournamentOperatorBracketPageParams = {},
): Promise<TournamentOperatorBracketPage> {
  const query = new URLSearchParams();
  if (params.sectionKey) query.set("section_key", params.sectionKey);
  if (params.cursor) query.set("cursor", params.cursor);
  if (params.limit !== undefined) query.set("limit", String(params.limit));
  const suffix = query.size > 0 ? `?${query.toString()}` : "";

  return httpClient.get<TournamentOperatorBracketPage>(
    `/tournaments/draws/${encodeURIComponent(drawId)}/operator-bracket${suffix}`,
  );
}

export function generateTeamBracket(drawId: string): Promise<TeamMatch[]> {
  return httpClient
    .post<unknown>(`/tournaments/draws/${drawId}/generate-team-bracket`)
    .then(asArray<TeamMatch>);
}

export function listTeamMatches(
  drawId: string,
  page?: number,
  pageSize?: number,
): Promise<Page<TeamMatch>> {
  return httpClient
    .get<unknown>(`/draws/${drawId}/team-matches${pageQuery(page, pageSize)}`)
    .then(asPage<TeamMatch>);
}

export function getTeamMatch(teamMatchId: string): Promise<TeamMatch> {
  return httpClient.get<TeamMatch>(`/team-matches/${teamMatchId}`);
}

export function listRubbers(teamMatchId: string): Promise<Rubber[]> {
  return httpClient
    .get<unknown>(`/tournaments/team-matches/${teamMatchId}/rubbers`)
    .then(asArray<Rubber>);
}

export function replaceTeamMatchLineup(
  teamMatchId: string,
  data: TeamMatchLineupUpdate,
): Promise<TeamMatchLineupResponse> {
  return httpClient.put<TeamMatchLineupResponse>(
    `/tournaments/team-matches/${encodeURIComponent(teamMatchId)}/lineup`,
    data,
  );
}

export function recordTeamMatchScore(
  teamMatchId: string,
  data: TeamMatchScoreUpdate,
): Promise<TeamMatch> {
  return httpClient.put<TeamMatch>(
    `/tournaments/team-matches/${encodeURIComponent(teamMatchId)}/score`,
    data,
  );
}

export function recordRubberScore(
  teamMatchId: string,
  rubberNumber: number,
  data: RubberScoreUpdate,
): Promise<Rubber> {
  return httpClient.put<Rubber>(
    `/tournaments/team-matches/${encodeURIComponent(teamMatchId)}/rubbers/${rubberNumber}/score`,
    data,
  );
}

export function createCourt(
  tournamentId: string,
  data: TournamentCourtCreate,
): Promise<TournamentCourt> {
  return httpClient.post<TournamentCourt>(
    `/tournaments/${tournamentId}/courts`,
    data,
  );
}

export function listCourts(tournamentId: string): Promise<TournamentCourt[]> {
  return httpClient
    .get<unknown>(`/tournaments/${tournamentId}/courts`)
    .then(asArray<TournamentCourt>);
}

export function deleteCourt(courtId: string): Promise<void> {
  return httpClient.delete<void>(`/courts/${courtId}`);
}

export function createTeam(
  tournamentId: string,
  eventId: string,
  data: TeamCreate,
): Promise<TeamResponse> {
  return httpClient.post<TeamResponse>(
    `/tournaments/${tournamentId}/events/${eventId}/teams`,
    {
      ...data,
      event_id: eventId,
    },
  );
}

export function listTeams(
  tournamentId: string,
  eventId?: string,
  page?: number,
  pageSize?: number,
): Promise<Page<TeamResponse>> {
  const basePath = eventId
    ? `/tournaments/${tournamentId}/events/${eventId}/teams`
    : `/tournaments/${tournamentId}/teams`;

  return httpClient
    .get<unknown>(`${basePath}${pageQuery(page, pageSize)}`)
    .then(asPage<TeamResponse>);
}

export function getTeam(teamId: string): Promise<TeamResponse> {
  return httpClient.get<TeamResponse>(`/teams/${teamId}`);
}

export function updateTeam(
  teamId: string,
  data: TeamUpdate,
): Promise<TeamResponse> {
  return httpClient.patch<TeamResponse>(`/teams/${teamId}`, data);
}

export function deleteTeam(teamId: string): Promise<void> {
  return httpClient.delete<void>(`/teams/${teamId}`);
}

export function addTeamMember(
  teamId: string,
  data: TeamMemberCreate,
): Promise<TeamMemberResponse> {
  return httpClient.post<TeamMemberResponse>(`/teams/${teamId}/members`, data);
}

export function removeTeamMember(
  teamId: string,
  userId: string,
): Promise<void> {
  return httpClient.delete<void>(`/teams/${teamId}/members/${userId}`);
}

export function listTeamMembers(teamId: string): Promise<TeamMemberResponse[]> {
  return httpClient
    .get<unknown>(`/teams/${teamId}/members`)
    .then(asArray<TeamMemberResponse>);
}

export function enterTeamDraw(
  teamId: string,
  data: TeamDrawEntryCreate,
): Promise<TeamDrawEntryResponse> {
  return httpClient.post<TeamDrawEntryResponse>(`/teams/${teamId}/draws`, data);
}

export function withdrawTeam(entryId: string): Promise<void> {
  return httpClient.delete<void>(`/team-entries/${entryId}`);
}

export function registerCoach(
  tournamentId: string,
  data: CoachRegistrationCreate,
): Promise<CoachRegistration> {
  return httpClient.post<CoachRegistration>(
    `/tournaments/${tournamentId}/coaches`,
    data,
  );
}

export function listCoaches(
  tournamentId: string,
): Promise<CoachRegistration[]> {
  return httpClient
    .get<unknown>(`/tournaments/${tournamentId}/coaches`)
    .then(asArray<CoachRegistration>);
}

export function approveCoach(
  tournamentId: string,
  coachEntryId: string,
): Promise<CoachRegistration> {
  return httpClient.patch<CoachRegistration>(
    `/tournaments/${tournamentId}/coaches/${coachEntryId}/approve`,
  );
}

export function assignStaff(
  tournamentId: string,
  data: TournamentStaffCreate,
): Promise<TournamentStaff> {
  return httpClient.post<TournamentStaff>(
    `/tournaments/${tournamentId}/staff`,
    data,
  );
}

export function listStaff(tournamentId: string): Promise<TournamentStaff[]> {
  return httpClient
    .get<unknown>(`/tournaments/${tournamentId}/staff/manage`)
    .then(asArray<TournamentStaff>);
}

export function removeStaff(
  tournamentId: string,
  staffId: string,
): Promise<void> {
  return httpClient.delete<void>(
    `/tournaments/${tournamentId}/staff/${staffId}`,
  );
}

export function applyMediaCredential(
  tournamentId: string,
  data: MediaCredentialApply,
): Promise<MediaCredential> {
  return httpClient.post<MediaCredential>(
    `/tournaments/${tournamentId}/media-credentials/apply`,
    data,
  );
}

export function listMediaCredentials(
  tournamentId: string,
): Promise<MediaCredential[]> {
  // Backend only exposes the public media credential list here; there is no operator/all query param.
  return httpClient
    .get<unknown>(`/tournaments/${tournamentId}/media-credentials`)
    .then(asArray<MediaCredential>);
}

export function approveMediaCredential(
  tournamentId: string,
  credentialId: string,
  notes?: string | null,
): Promise<MediaCredential> {
  return httpClient.patch<MediaCredential>(
    `/tournaments/${tournamentId}/media-credentials/${credentialId}/approve${notesQuery(notes)}`,
  );
}

export function denyMediaCredential(
  tournamentId: string,
  credentialId: string,
  notes?: string | null,
): Promise<MediaCredential> {
  return httpClient.patch<MediaCredential>(
    `/tournaments/${tournamentId}/media-credentials/${credentialId}/deny${notesQuery(notes)}`,
  );
}

export function createLegalDocument(
  tournamentId: string,
  data: LegalDocumentCreate,
): Promise<LegalDocument> {
  return httpClient.post<LegalDocument>(
    `/tournaments/${tournamentId}/legal-documents`,
    data,
  );
}

export function listLegalDocuments(
  tournamentId: string,
): Promise<LegalDocument[]> {
  return httpClient
    .get<unknown>(`/tournaments/${tournamentId}/legal-documents`)
    .then(asArray<LegalDocument>);
}

export function createCertificateTemplate(
  tournamentId: string,
  data: CertificateTemplateCreate,
): Promise<CertificateTemplate> {
  return httpClient.post<CertificateTemplate>(
    `/tournaments/${tournamentId}/certificate-templates`,
    data,
  );
}

export function listCertificateTemplates(
  tournamentId: string,
): Promise<CertificateTemplate[]> {
  return httpClient
    .get<unknown>(`/tournaments/${tournamentId}/certificate-templates`)
    .then(asArray<CertificateTemplate>);
}

export function addMerchandise(
  tournamentId: string,
  data: MerchandiseCreate,
): Promise<Merchandise> {
  return httpClient.post<Merchandise>(
    `/tournaments/${tournamentId}/merchandise`,
    data,
  );
}

export function listMerchandise(tournamentId: string): Promise<Merchandise[]> {
  return httpClient
    .get<unknown>(`/tournaments/${tournamentId}/merchandise`)
    .then(asArray<Merchandise>);
}

export function orderMerchandise(
  tournamentId: string,
  orders: MerchandiseOrderCreate[],
): Promise<MerchandiseOrder[]> {
  return httpClient
    .post<unknown>(`/tournaments/${tournamentId}/merchandise/orders`, orders)
    .then(asArray<MerchandiseOrder>);
}

export function listMerchandiseOrders(
  tournamentId: string,
): Promise<MerchandiseOrder[]> {
  return httpClient
    .get<unknown>(`/tournaments/${tournamentId}/merchandise/orders`)
    .then(asArray<MerchandiseOrder>);
}

export function requestProspectusUpload(
  tournamentId: string,
): Promise<MediaUploadResponse> {
  return httpClient.post<MediaUploadResponse>(
    `/media/tournaments/${tournamentId}/prospectus`,
  );
}

export async function uploadToPresignedUrl(
  ticket: MediaUploadResponse,
  file: File,
): Promise<void> {
  if (ticket.method === "POST") {
    await uploadFileWithTicket(file, ticket);
    return;
  }

  if (ticket.method !== "PUT") {
    throw new Error(`Unsupported upload method: ${ticket.method}`);
  }

  const res = await fetch(ticket.upload_url, {
    method: "PUT",
    headers: file.type ? { "Content-Type": file.type } : undefined,
    body: file,
    credentials: "omit",
  });
  if (!res.ok) {
    throw new Error(`Upload failed with status ${res.status}`);
  }
}
